import { HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { FileDownloadInfo, ReportHistory } from '@dpr/models';
import {
  HttpSharedContext,
  HttpSharedFileContext,
  Rights,
} from '@shared/enums';
import {
  FileUpload,
  OfflineSyncResponse,
  ProjectSetup,
  Subproject,
  UserSession,
} from '@shared/models';
import { DprDetails, DprDetailsSync } from '@shared/models/dpr';
import { Observable, of, throwError } from 'rxjs';
import { BasePersistenceService } from '.';
import { GenericHttpClientService } from '../generic-http-client.service';
import { OfflineSyncSanitizeService } from '../offline-sync-sanitize.service';
import { subprojectAddressHeaders } from '@shared/utils';

@Injectable()
export class RemotePersistenceService extends BasePersistenceService {
  constructor(
    private genericHttpClientService: GenericHttpClientService,
    private offlineSyncSanitizeService: OfflineSyncSanitizeService
  ) {
    super();
  }

  // user session

  getUserSession(): Observable<UserSession> {
    return this.genericHttpClientService.get<UserSession>({
      context: HttpSharedContext.GetUserSession,
    });
  }

  getProjectSetup(subprojectId?: number): Observable<ProjectSetup> {
    return this.genericHttpClientService.get<ProjectSetup>({
      context: HttpSharedContext.GetProjectSetup,
      headers: subprojectId
        ? subprojectAddressHeaders(subprojectId)
        : undefined,
    });
  }

  getLastThreeDprs(subprojectId?: number): Observable<DprDetails[]> {
    return this.genericHttpClientService.get<DprDetails[]>({
      context: HttpSharedContext.GetLastThreeDprs,
      headers: subprojectId
        ? subprojectAddressHeaders(subprojectId)
        : undefined,
    });
  }

  updateSelectedClient(clientId: string): Observable<UserSession> {
    return this.genericHttpClientService.post<
      UserSession,
      { [key: string]: string }
    >({
      context: HttpSharedContext.SyncSelectedClient,
      body: {
        id: clientId,
      },
    });
  }

  updateSelectedSubproject(subprojectId: number): Observable<void> {
    return this.genericHttpClientService.post<void>({
      context: HttpSharedContext.SyncSelectedSubproject,
      headers: subprojectAddressHeaders(subprojectId),
    });
  }

  // files

  uploadFile(formData: FormData, uploadConfig: any): Observable<any> {
    const config = uploadConfig || {};
    return this.genericHttpClientService.post<FormData, FormData>({
      context: HttpSharedFileContext.UploadFile,
      body: formData,
      args: {
        ...config,
      },
      responseType: 'text',
    });
  }

  downloadFile(externalUrl: string, fileName: string): Observable<any> {
    if (!fileName || !externalUrl) {
      return throwError(
        () => new Error('File name or external URL not defined')
      );
    }

    return this.genericHttpClientService.get<{ [key: string]: string }>({
      context: HttpSharedFileContext.SharedDownloadFile,
      args: {
        externalUrl,
        fileName,
      },
      responseType: 'blob',
    });
  }

  downloadZipFile(fileDownloadInfo: FileDownloadInfo[]): Observable<string> {
    return this.genericHttpClientService.post<string, FileDownloadInfo[]>({
      context: HttpSharedFileContext.DownloadZipArchive,
      body: fileDownloadInfo,
      responseType: 'text',
    });
  }

  getDprReportHistory(dprId: number | string): Observable<ReportHistory> {
    return this.genericHttpClientService.get<ReportHistory>({
      context: HttpSharedContext.GetDprReportHistory,
      args: {
        dprId,
      },
    });
  }

  getUserManualDetails(clientId: string): Observable<FileUpload> {
    if (!clientId) {
      // No support record configured for this client, so there is nothing to address the
      // request at — skip the request rather than send a header HttpHeaders can't represent.
      return of(null);
    }

    return this.genericHttpClientService.get<FileUpload>({
      context: HttpSharedContext.GetUserManual,
      // The Clients grid reads one row's client, which is not always the selected one, so this
      // request addresses its own client instead of taking the interceptor's.
      headers: new HttpHeaders({ ClientId: clientId }),
    });
  }

  syncOfflineData(
    syncOfflineGuid: string,
    dprDetailsOfflineChanges: DprDetailsSync[],
    changesSubproject: Subproject | undefined
  ): Observable<OfflineSyncResponse[]> {
    // Strip client-generated temporary ids (GUIDs/composites) from offline-created records; the new
    // API binds these into int/int? id & FK fields and 400s on a non-numeric string. See LAUTEC-7644.
    const sanitizedChanges =
      this.offlineSyncSanitizeService.sanitizeOfflineChanges(
        dprDetailsOfflineChanges
      );

    // The changes go to the subproject they were made in. That is the selected one unless the user
    // switched in the moment between coming back online and the "back online" dialog opening; the
    // dialog's backdrop then covers the subproject picker until it is closed. Header and body name
    // the same subproject, since the API refuses a request where they differ.
    const subprojectId = sanitizedChanges[0].dprSubprojectId;

    const postData = {
      dprDetailsList: sanitizedChanges,
      subprojectId,
    };

    // A subproject missing from the session is one the user can no longer open. The full sync still
    // goes out, so the API refuses it and the user is told the data could not be synced.
    const isAllowedToEditDprs =
      changesSubproject?.dprScreen === Rights.Edit &&
      !changesSubproject.isLocked;

    return this.genericHttpClientService.post<
      OfflineSyncResponse[],
      {
        dprDetailsList: DprDetailsSync[];
        subprojectId: number;
      }
    >({
      context:
        !isAllowedToEditDprs && changesSubproject?.canEditExternalPartyComment
          ? HttpSharedContext.SyncOfflineChangesExternalComments
          : HttpSharedContext.SyncOfflineChanges,
      body: postData,
      args: {
        syncOfflineGuid,
      },
      headers: subprojectAddressHeaders(subprojectId),
    });
  }
}
